import { ListItem, Button, Grid, Typography, Chip, Tooltip, Box } from '@mui/material';
import { toHourMinutes, toWeekDayDateMonYear } from '~/modules/helpers';
import { Carrier, FlightReservation, TravelClassOption } from '~/types';
import InfoIcon from '@mui/icons-material/Info';
import CarrierLogo from './CarrierLogo';
import { BOOKING_CONTEXT, FLIGHT_TYPE, TRAVEL_CLASS } from '~/literals';
import HailIcon from '@mui/icons-material/Hail';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import FlightClassIcon from '@mui/icons-material/FlightClass';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';

interface ReservationProps {
  reservation: FlightReservation;
  onViewButtonClicked: (id: string) => void;
}

const Reservation = ({ reservation, onViewButtonClicked }: ReservationProps) => {
  const flightType = reservation.bookingDetails.offers[0].flightType;

  const outbound = reservation.bookingDetails.offers[0].itineraries.outbound;
  const inbound = flightType == FLIGHT_TYPE.RETURN ? reservation.bookingDetails.offers[0].itineraries.inbound : null;

  const outboundLegs = outbound.legs;
  const inboundLegs = inbound ? inbound.legs : [];

  const departure = outboundLegs[0].departure;
  const arrival = outboundLegs[outboundLegs.length - 1].arrival;

  let carriersSet = new Set<string>();
  let carriers: Carrier[] = [];
  outboundLegs.forEach(leg => {
    if (!carriersSet.has(leg.operatedBy.code)) {
      carriersSet.add(leg.operatedBy.code);
      carriers.push(leg.operatedBy);
    }
  });

  inboundLegs.forEach(leg => {
    if (!carriersSet.has(leg.operatedBy.code)) {
      carriersSet.add(leg.operatedBy.code);
      carriers.push(leg.operatedBy);
    }
  });

  let cabinSet = new Set<string>();
  let cabins: String[] = [];
  reservation.bookingDetails.offers.forEach(offer => {
    offer.passengerPricing.forEach(pp => {
      pp.fareDetailsByTripLeg.forEach(fdbtl => {
        if (!cabinSet.has(fdbtl.cabin)) {
          cabinSet.add(fdbtl.cabin)
          cabins.push(TRAVEL_CLASS[fdbtl.cabin as keyof TravelClassOption]);
        }
      })
    });
  });

  const justItinerary = reservation.bookingContext === BOOKING_CONTEXT.FLIGHT_ITINERARY;
  const passengersLabel = reservation.bookingDetails.passengers.length > 1 ?
    `${reservation.bookingDetails.passengers.length} passengers` :
    `${reservation.bookingDetails.passengers.length} passenger`;


  const flightTypeLabel = flightType == FLIGHT_TYPE.ONE_WAY ? 'One Way' : 'Return';
  const flightTypeIcon = flightType == FLIGHT_TYPE.ONE_WAY ? <ArrowRightAltIcon /> : <ImportExportIcon sx={{ transform: 'rotate(90deg)' }} />;

  return (
    <ListItem sx={{ marginBottom: 4 }}>
      <Grid container spacing={0}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
              {carriers.map((carrier: Carrier, i) => (
                <Box
                  key={i}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 50,
                    height: 50,
                    borderRadius: '50%',
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                    background: '#fff',
                    marginLeft: -1,
                    zIndex: 10 - i,
                    position: 'relative',
                  }}
                >
                  <CarrierLogo
                    boxStyle={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                    }}
                    imageStyle={{
                      height: 'auto',
                      width: '80%',
                    }}
                    carrier={carrier}
                    hideText
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </Grid>
        <Grid item xs={4}>
          <Typography variant='h6'>{toWeekDayDateMonYear(departure.dateTime)}</Typography>
          <Typography variant='body2'><strong>{toHourMinutes(departure.dateTime)}</strong> {departure.location.friendlyName}({departure.location.code})</Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant='h6'>{toWeekDayDateMonYear(arrival.dateTime)}</Typography>
          <Typography variant='body2'><strong>{toHourMinutes(arrival.dateTime)}</strong> {arrival.location.friendlyName}({arrival.location.code})</Typography>
        </Grid>
        <Grid item xs={2}>
          <Tooltip title={justItinerary ?
            'This is just an itinerary e.g. can be used when applying for visas. Contact us if you need help to ticket this flight for travel.' :
            'Ticketed and confirmed for travel.'}>
            <Chip icon={<InfoIcon />} label={justItinerary ? 'Not for travel' : 'Ticketed for travel'} color={justItinerary ? 'warning' : 'success'} />
          </Tooltip>
        </Grid>
        <Grid item xs={2}>
          <Button variant="contained" color="secondary" onClick={() => onViewButtonClicked(reservation.reservationId)}>
            View Trip
          </Button>
        </Grid>
        <Grid item xs={12}>
          <Box marginTop={1}>
            <Chip size="small" icon={<HailIcon />} label={passengersLabel} sx={{ marginRight: 1 }} />
            <Chip size="small" icon={flightTypeIcon} label={flightTypeLabel} sx={{ marginRight: 1 }} />
            {
              cabins.map(cabin => (
                <Chip size="small" icon={<FlightClassIcon />} label={cabin} sx={{ marginRight: 1 }} key={cabin as string} />
              ))
            }
          </Box>
        </Grid>
      </Grid>
    </ListItem>
  );
};

export default Reservation;
